import {
  CounterFiberContent,
  CounterFabricFormValues,
  CounterFabric,
  CounterQuote,
  GarmentFabricDetails,
  GarmentFabricDetailsForAPI,
  SecondaryFabric,
  SecondaryFabricType,
} from '../types';

type RawCounterData = Partial<CounterQuote> & {
  fabric_content?: string;
  fabric_content_list?: CounterFabric[];
};

type MigratedCounterData = Omit<RawCounterData, 'fabric_content_list'> & {
  fabric_content_list: CounterFiberContent[];
};

type DataWithFiberContent = {
  fabric_content?: string;
  fabric_content_list?: CounterFiberContent[];
};

type APIReadyCounterData = Omit<
  CounterFabricFormValues,
  | 'fabric_content_list'
  | 'weight'
  | 'contrast'
  | 'lining'
  | 'filling'
  | 'backing'
  | 'coating'
> & {
  fabric_content?: string;
  fabric_content_list?: CounterFabric[];
  weight: number | '';
  isFabricWeightNotApplicable?: boolean;
  contrast?: {
    contrastWeight?: number;
    contrastContent: CounterFabric[];
  };
  lining?: {
    liningWeight?: number;
    liningContent: CounterFabric[];
  };
  filling?: {
    fillingWeight?: number;
    fillingContent: CounterFabric[];
  };
  // Legacy-only types (not available for new entries, but supported for existing data)
  backing?: {
    backingWeight?: number;
    backingContent: CounterFabric[];
  };
  coating?: {
    coatingWeight?: number;
    coatingContent: CounterFabric[];
  };
  hasPolyurethaneCoating?: boolean;
};

/**
 * Migration utility to convert legacy fabric_content to new fabric_content_list format
 * for backward compatibility with existing counters
 */
const migrateFabricContent = (counter: RawCounterData): MigratedCounterData => {
  // If fabric_content_list already exists, add React IDs
  if (counter.fabric_content_list) {
    return {
      ...counter,
      fabric_content_list: counter.fabric_content_list.map((fiber, index) => ({
        id: `fiber-${index}`,
        ...fiber,
      })),
    };
  }

  // If legacy fabric_content exists, migrate it to fabric_content_list
  if (counter.fabric_content) {
    const migratedCounter = {
      ...counter,
      fabric_content_list: [
        {
          id: `fiber-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          name: counter.fabric_content,
          percentage: 100,
        },
      ],
    };

    return migratedCounter;
  }

  // New counter - initialize with empty fiber content
  return {
    ...counter,
    fabric_content_list: [
      {
        id: `fiber-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        name: '',
        percentage: 0,
      },
    ],
  };
};

/**
 * Utility to prepare counter data for API submission
 * Ensures both legacy and new fields are populated for compatibility
 */
const prepareFabricDataForAPI = <T extends DataWithFiberContent>(
  formData: T,
): T & { fabric_content?: string } => {
  const prepared = { ...formData };

  if (prepared.fabric_content_list && prepared.fabric_content_list.length > 0) {
    const firstFiber = prepared.fabric_content_list[0];

    if (prepared.fabric_content_list.length === 1) {
      prepared.fabric_content = firstFiber.name;
    } else {
      prepared.fabric_content = prepared.fabric_content_list
        .map(
          (fiber: CounterFiberContent) =>
            `${fiber.name} (${fiber.percentage}%)`,
        )
        .join(', ');
    }
  }

  return prepared;
};

/**
 * Remove React IDs from fiber content for API
 */
const cleanFiberContentForAPI = (
  fibers: CounterFiberContent[],
): CounterFabric[] =>
  fibers.map(({ name, percentage }) => ({ name, percentage }));

/**
 * Add React IDs to fiber content from API
 */
const addReactIDsToFiberContent = (
  fibers: CounterFabric[],
): CounterFiberContent[] =>
  fibers.map((fiber, index) => ({
    id: `fiber-${index}`,
    ...fiber,
  }));

const convertWeightToNumber = (
  weight: number | string | undefined,
): number | undefined => {
  if (weight == null) return undefined;
  const converted = Number(weight);
  return isNaN(converted) ? undefined : converted;
};

const convertWeightToString = (
  weight: number | undefined,
): string | undefined => weight?.toString();

/**
 * Transform fabric sections for API submission
 */
const transformFabricSectionsForAPI = (
  formData: CounterFabricFormValues,
): Partial<
  Pick<
    APIReadyCounterData,
    'contrast' | 'lining' | 'filling' | 'backing' | 'coating'
  >
> => {
  const sections: Partial<
    Pick<
      APIReadyCounterData,
      'contrast' | 'lining' | 'filling' | 'backing' | 'coating'
    >
  > = {};

  if (formData.contrast) {
    sections.contrast = {
      contrastWeight: convertWeightToNumber(formData.contrast.contrastWeight),
      contrastContent: cleanFiberContentForAPI(
        formData.contrast.contrastContent,
      ),
    };
  }

  if (formData.lining) {
    sections.lining = {
      liningWeight: convertWeightToNumber(formData.lining.liningWeight),
      liningContent: cleanFiberContentForAPI(formData.lining.liningContent),
    };
  }

  if (formData.filling) {
    sections.filling = {
      fillingWeight: convertWeightToNumber(formData.filling.fillingWeight),
      fillingContent: cleanFiberContentForAPI(formData.filling.fillingContent),
    };
  }

  // Legacy-only types (for existing data, can't add new)
  if (formData.backing) {
    sections.backing = {
      backingWeight: convertWeightToNumber(formData.backing.backingWeight),
      backingContent: cleanFiberContentForAPI(formData.backing.backingContent),
    };
  }

  if (formData.coating) {
    sections.coating = {
      coatingWeight: convertWeightToNumber(formData.coating.coatingWeight),
      coatingContent: cleanFiberContentForAPI(formData.coating.coatingContent),
    };
  }

  return sections;
};

/**
 * Process fabric sections for form initialization
 */
const processFabricSectionsForForm = (
  response: RawCounterData,
): Partial<
  Pick<
    CounterFabricFormValues,
    'contrast' | 'lining' | 'filling' | 'backing' | 'coating'
  >
> => {
  const sections: Partial<
    Pick<
      CounterFabricFormValues,
      'contrast' | 'lining' | 'filling' | 'backing' | 'coating'
    >
  > = {};

  if (response.contrast) {
    sections.contrast = {
      contrastWeight: convertWeightToString(response.contrast.contrastWeight),
      contrastContent: addReactIDsToFiberContent(
        response.contrast.contrastContent,
      ),
    };
  }

  if (response.lining) {
    sections.lining = {
      liningWeight: convertWeightToString(response.lining.liningWeight),
      liningContent: addReactIDsToFiberContent(response.lining.liningContent),
    };
  }

  if (response.filling) {
    sections.filling = {
      fillingWeight: convertWeightToString(response.filling.fillingWeight),
      fillingContent: addReactIDsToFiberContent(
        response.filling.fillingContent,
      ),
    };
  }

  // Legacy-only types (for existing data)
  if (response.backing) {
    sections.backing = {
      backingWeight: convertWeightToString(response.backing.backingWeight),
      backingContent: addReactIDsToFiberContent(
        response.backing.backingContent,
      ),
    };
  }

  if (response.coating) {
    sections.coating = {
      coatingWeight: convertWeightToString(response.coating.coatingWeight),
      coatingContent: addReactIDsToFiberContent(
        response.coating.coatingContent,
      ),
    };
  }

  return sections;
};

/**
 * API preparation - clean up React IDs and convert types
 */
export const prepareCounterFormDataForAPI = (
  formData: CounterFabricFormValues,
): APIReadyCounterData => {
  const prepared = prepareFabricDataForAPI(formData);

  const isFabricWeightNA = formData.isFabricWeightNotApplicable ?? false;
  const isMultiGarment = formData.isMultiGarment ?? false;
  const weightValue = convertWeightToNumber(formData.weight);

  const weight: number | '' =
    isMultiGarment || isFabricWeightNA || !isFinite(weightValue ?? Infinity)
      ? ''
      : (weightValue ?? 0);

  const apiData: APIReadyCounterData = {
    fabric_type: formData.fabric_type,
    weight,
    fabric_code_name: formData.fabric_code_name,
    fabric_content: prepared.fabric_content,
    fabric_content_list: prepared.fabric_content_list
      ? cleanFiberContentForAPI(prepared.fabric_content_list)
      : undefined,
    isFabricWeightNotApplicable: isFabricWeightNA,
    hasPolyurethaneCoating: formData.hasPolyurethaneCoating,

    ...transformFabricSectionsForAPI(formData),
  };

  return apiData;
};

/**
 * Response transformation - just add React IDs and convert types for forms
 */
export const transformAPIResponseToCounterForm = (
  response: RawCounterData,
): Partial<CounterFabricFormValues> => {
  const migrated = migrateFabricContent(response);

  const isFabricWeightNotApplicable =
    response.isFabricWeightNotApplicable ??
    (response.fabric_weight === '' ||
      response.fabric_weight === null ||
      response.fabric_weight === undefined);

  const transformed: Partial<CounterFabricFormValues> = {
    fabric_type: migrated.fabric_type,
    weight: convertWeightToNumber(migrated.fabric_weight),
    fabric_code_name: migrated.fabric_code_name,
    fabric_content: migrated.fabric_content,
    fabric_content_list: migrated.fabric_content_list,
    isFabricWeightNotApplicable,
    hasPolyurethaneCoating: response.hasPolyurethaneCoating,

    ...processFabricSectionsForForm(response),

    secondaryFabrics: response.secondaryFabrics?.map((sf) => ({
      ...sf,
      content:
        sf.content?.map((fc, i) => ({
          ...fc,
          id: fc.id || `fiber-${i}`,
        })) || [],
    })),

    // Pass through multi-garment fields (with React IDs added)
    isMultiGarment: response.isMultiGarment,
    garments: response.garments?.map((garment) => ({
      ...garment,
      fabricContentList:
        garment.fabricContentList?.map((fc, i) => ({
          ...fc,
          id: fc.id || `fiber-${i}`,
        })) || [],
      secondaryFabrics:
        garment.secondaryFabrics?.map((sf) => ({
          ...sf,
          content:
            sf.content?.map((fc, i) => ({
              ...fc,
              id: fc.id || `fiber-${i}`,
            })) || [],
        })) || [],
    })),
  };

  return transformed;
};

/**
 * Clean garments array for API submission
 * - Strips React IDs from fabricContentList and secondaryFabrics content
 * - Ensures fabricWeight is a string (API expects string)
 */
export const cleanGarmentsForAPI = (
  garments: GarmentFabricDetails[],
): GarmentFabricDetailsForAPI[] =>
  garments.map((garment) => ({
    garmentIndex: garment.garmentIndex,
    garmentPart: garment.garmentPart,
    fabricContentList: garment.fabricContentList
      ? cleanFiberContentForAPI(garment.fabricContentList)
      : undefined,
    fabricWeight:
      garment.fabricWeight !== undefined
        ? String(garment.fabricWeight)
        : undefined,
    fabricCodeName: garment.fabricCodeName,
    isFabricWeightNotApplicable: garment.isFabricWeightNotApplicable,
    hasPolyurethaneCoating: garment.hasPolyurethaneCoating,
    secondaryFabrics: garment.secondaryFabrics?.map((sf) => ({
      type: sf.type,
      instanceIndex: sf.instanceIndex,
      content: sf.content ? cleanFiberContentForAPI(sf.content) : [],
      weight: sf.weight,
      hasPolyurethaneCoating: sf.hasPolyurethaneCoating,
    })),
  }));

// ============================================================
// Multi-Garment Support Utilities
// ============================================================

/**
 * Normalize legacy counter quote format to the new multi-garment format.
 * This converts individual secondary fabric fields (lining, contrast, etc.)
 * into the unified garments array structure.
 */
export const normalizeToGarments = (
  quote: Partial<CounterQuote>,
): GarmentFabricDetails[] => {
  // If already in new format with garments, return as-is (with React IDs preserved or added)
  if (quote.garments && quote.garments.length > 0) {
    return quote.garments.map((garment) => ({
      ...garment,
      fabricContentList:
        garment.fabricContentList?.map((fc, i) => ({
          ...fc,
          id: fc.id || `fiber-${i}`,
        })) || [],
      secondaryFabrics:
        garment.secondaryFabrics?.map((sf) => ({
          ...sf,
          content:
            sf.content?.map((fc, i) => ({
              ...fc,
              id: fc.id || `fiber-${i}`,
            })) || [],
        })) || [],
    }));
  }

  // Transform legacy secondary fabric fields to SecondaryFabric array
  const secondaryFabrics: SecondaryFabric[] = [];

  const legacyFabricTypes: {
    type: SecondaryFabricType;
    data?: { weight?: number; content: CounterFabric[] };
  }[] = [
    {
      type: 'lining',
      data: quote.lining
        ? {
            weight: quote.lining.liningWeight,
            content: quote.lining.liningContent,
          }
        : undefined,
    },
    {
      type: 'contrast',
      data: quote.contrast
        ? {
            weight: quote.contrast.contrastWeight,
            content: quote.contrast.contrastContent,
          }
        : undefined,
    },
    {
      type: 'filling',
      data: quote.filling
        ? {
            weight: quote.filling.fillingWeight,
            content: quote.filling.fillingContent,
          }
        : undefined,
    },
    // Legacy-only types (for existing data)
    {
      type: 'backing',
      data: quote.backing
        ? {
            weight: quote.backing.backingWeight,
            content: quote.backing.backingContent,
          }
        : undefined,
    },
    {
      type: 'coating',
      data: quote.coating
        ? {
            weight: quote.coating.coatingWeight,
            content: quote.coating.coatingContent,
          }
        : undefined,
    },
  ];

  for (const fabric of legacyFabricTypes) {
    if (fabric.data && fabric.data.content && fabric.data.content.length > 0) {
      secondaryFabrics.push({
        type: fabric.type,
        instanceIndex: 1,
        content: fabric.data.content.map((fc, i) => ({
          id: `fiber-${i}`,
          ...fc,
        })),
        weight: fabric.data.weight,
      });
    }
  }

  // Create the default garment from legacy data
  const defaultGarment: GarmentFabricDetails = {
    garmentIndex: 1,
    garmentPart: 'other',
    fabricContentList:
      quote.fabric_content_list?.map((fc, i) => ({
        id: `fiber-${i}`,
        ...fc,
      })) || [],
    fabricWeight: quote.fabric_weight,
    fabricCodeName: quote.fabric_code_name,
    secondaryFabrics,
  };

  return [defaultGarment];
};

/**
 * Denormalize from new multi-garment format back to legacy format.
 * This is useful for backward compatibility with systems that don't support
 * the new format yet.
 */
export const denormalizeFromGarments = (
  garments: GarmentFabricDetails[],
): Partial<CounterQuote> => {
  if (!garments || garments.length === 0) {
    return {};
  }

  // Use first garment for legacy fields
  const primaryGarment = garments[0];
  const result: Partial<CounterQuote> = {
    fabric_content_list: primaryGarment.fabricContentList?.map(
      ({ name, percentage }) => ({ name, percentage }),
    ),
    fabric_weight: primaryGarment.fabricWeight,
    fabric_code_name: primaryGarment.fabricCodeName,
  };

  // Convert secondary fabrics back to legacy format (only first instance of each type)
  if (primaryGarment.secondaryFabrics) {
    for (const sf of primaryGarment.secondaryFabrics) {
      // Only use first instance for legacy
      if (sf.instanceIndex === 1) {
        const cleanContent = sf.content?.map(({ name, percentage }) => ({
          name,
          percentage,
        }));

        switch (sf.type) {
          case 'lining':
            if (!result.lining && cleanContent) {
              result.lining = {
                liningContent: cleanContent,
                liningWeight: convertWeightToNumber(sf.weight),
              };
            }
            break;
          case 'contrast':
            if (!result.contrast && cleanContent) {
              result.contrast = {
                contrastContent: cleanContent,
                contrastWeight: convertWeightToNumber(sf.weight),
              };
            }
            break;
          case 'filling':
            if (!result.filling && cleanContent) {
              result.filling = {
                fillingContent: cleanContent,
                fillingWeight: convertWeightToNumber(sf.weight),
              };
            }
            break;
          // Legacy-only types (for existing data)
          case 'backing':
            if (!result.backing && cleanContent) {
              result.backing = {
                backingContent: cleanContent,
                backingWeight: convertWeightToNumber(sf.weight),
              };
            }
            break;
          case 'coating':
            if (!result.coating && cleanContent) {
              result.coating = {
                coatingContent: cleanContent,
                coatingWeight: convertWeightToNumber(sf.weight),
              };
            }
            break;
        }
      }
    }
  }

  return result;
};

/**
 * Generate a unique ID for a secondary fabric instance
 */
export const generateSecondaryFabricId = (): string =>
  `sf-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

/**
 * Get the next instance index for a secondary fabric type within a garment
 */
export const getNextInstanceIndex = (
  garment: GarmentFabricDetails,
  fabricType: SecondaryFabricType,
): number => {
  const existingOfType =
    garment.secondaryFabrics?.filter((sf) => sf.type === fabricType) || [];
  const maxIndex = existingOfType.reduce(
    (max, sf) => Math.max(max, sf.instanceIndex),
    0,
  );
  return maxIndex + 1;
};

/**
 * Get display label for a secondary fabric (e.g., "#1 Lining", "#2 Lining")
 */
export const getSecondaryFabricLabel = (
  type: SecondaryFabricType,
  instanceIndex: number,
): string => {
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
  return `#${instanceIndex} ${typeLabel}`;
};
